import { CompanySettings } from "../../app/modules/auth/core/_models";
import { Lang } from "./Streami18n";

export interface TerminologyLocalizedLabel {
  en: string;
  ar?: string | null;
}

export interface TerminologyResourceLabel {
  singular: TerminologyLocalizedLabel;
  plural?: TerminologyLocalizedLabel | null;
}

export interface OrganizationTerminology {
  resources?: Record<string, TerminologyResourceLabel>;
}

export type TerminologyResourceForm = "singular" | "plural";

/** i18n message ids replaced entirely when org terminology defines the mapped resource. */
export const TERMINOLOGY_MESSAGE_MAP: Record<
  string,
  { resource: string; form: TerminologyResourceForm }
> = {
  "MENU.CUSTOMERS": { resource: "consumer", form: "plural" },
  "MENU.BENEFICIARIES": { resource: "beneficiary", form: "plural" },
  "BENEFICIARY.ALL": { resource: "beneficiary", form: "plural" },
  /** Short standalone labels only — not “Customer name” (phrase swap keeps “name”). */
  "BENEFICIARY.CUSTOMER": { resource: "consumer", form: "singular" },
  "CUSTOMER.ASSOCIATED_ENTITY": { resource: "beneficiary", form: "singular" },
};

/** Replace default copy that mentions “associated entity” with the org beneficiary label. */
const ASSOCIATED_ENTITY_MESSAGE_IDS = new Set([
  "CUSTOMER.CREATE_ASSOCIATED_ENTITY",
  "CUSTOMER.EDIT_ASSOCIATED_ENTITY",
  "CUSTOMER.ASSOCIATED_ENTITY_NAME",
  "CUSTOMER.ASSOCIATED_ENTITY_NAME_REQUIRED",
  "CUSTOMER.ASSOCIATED_ENTITY_ID",
  "CUSTOMER.ASSOCIATED_ENTITY_ID_PLACEHOLDER",
]);

/** Customer-modal copy for linked beneficiaries (entities), not consumers. */
const BENEFICIARY_ENTITY_MESSAGE_IDS = new Set([
  "CUSTOMER.OPTIONAL_IDENTIFIERS",
  "CUSTOMER.ADD_NEW_ENTITY",
  "CUSTOMER.ADD_ANOTHER_IDENTIFIER",
  "CUSTOMER.REMOVE_IDENTIFIER",
]);

/** Settings → payment methods → global discount coupon rows. */
const SETTINGS_DISCOUNT_BENEFICIARY_MESSAGE_IDS = new Set([
  "SETTINGS.TAB.PAYMENT_METHODS.DISCOUNT_BENEFICIARY",
  "SETTINGS.TAB.PAYMENT_METHODS.ADD_ANOTHER_BENEFICIARY",
]);

const matchCase = (sourceWord: string, replacement: string): string => {
  if (sourceWord === sourceWord.toUpperCase()) {
    return replacement.toUpperCase();
  }
  if (sourceWord[0] === sourceWord[0]?.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement.charAt(0).toLowerCase() + replacement.slice(1);
};

const applyEnglishWordSwap = (
  defaultMessage: string,
  pairs: { pattern: RegExp; replacement: string }[]
): string => {
  let result = defaultMessage;
  for (const { pattern, replacement } of pairs) {
    result = result.replace(pattern, (match) => matchCase(match, replacement));
  }
  return result;
};

const applyBeneficiaryLabelToMessage = (
  defaultMessage: string,
  label: string
): string => {
  if (/an associated entity/i.test(defaultMessage)) {
    return defaultMessage.replace(/an associated entity/i, `a ${label}`);
  }
  return defaultMessage.replace(/associated entity/gi, (match) =>
    match[0] === match[0].toUpperCase()
      ? label
      : label.charAt(0).toLowerCase() + label.slice(1)
  );
};

const applyConsumerEnglishLabels = (
  defaultMessage: string,
  singular: string,
  plural: string
): string =>
  applyEnglishWordSwap(defaultMessage, [
    { pattern: /\bcustomers\b/gi, replacement: plural },
    { pattern: /\bcustomer\b/gi, replacement: singular },
  ]);

const applyBeneficiaryEnglishLabels = (
  defaultMessage: string,
  singular: string,
  plural: string
): string => {
  let result = applyEnglishWordSwap(defaultMessage, [
    { pattern: /\bassociated entities\b/gi, replacement: plural },
    { pattern: /\bassociated entity\b/gi, replacement: singular },
    { pattern: /\bnew entity\b/gi, replacement: `new ${singular}` },
    { pattern: /\bentities\b/gi, replacement: plural },
    { pattern: /\bentity\b/gi, replacement: singular },
    { pattern: /\bbeneficiaries\b/gi, replacement: plural },
    { pattern: /\bbeneficiary\b/gi, replacement: singular },
  ]);
  result = applyBeneficiaryLabelToMessage(result, singular);
  return result;
};

/** Longest phrases first — literal replace (safe for Arabic). */
type ArabicPhraseRule = {
  phrase: string;
  form: TerminologyResourceForm;
};

const AR_CONSUMER_PHRASES: ArabicPhraseRule[] = [
  { phrase: "العملاء", form: "plural" },
  { phrase: "عملاء", form: "plural" },
  { phrase: "العميل", form: "singular" },
  { phrase: "عميلًا", form: "singular" },
  { phrase: "عميل", form: "singular" },
  { phrase: "المستهلك", form: "singular" },
  { phrase: "مستهلك", form: "singular" },
];

const AR_BENEFICIARY_PHRASES: ArabicPhraseRule[] = [
  { phrase: "الكيانات المرتبطة", form: "plural" },
  { phrase: "كيانات مرتبطة", form: "plural" },
  { phrase: "كيان جديد", form: "singular" },
  { phrase: "كيان آخر", form: "singular" },
  { phrase: "الكيان المرتبط", form: "singular" },
  { phrase: "كيان مرتبط", form: "singular" },
  { phrase: "المستفيدون", form: "plural" },
  { phrase: "المستفيدين", form: "plural" },
  { phrase: "مستفيدين", form: "plural" },
  { phrase: "المستفيد", form: "singular" },
  { phrase: "مستفيد", form: "singular" },
];

/** Org terminology Arabic labels are used verbatim (no auto «ال» prefix). */
const applyArabicPhraseRules = (
  defaultMessage: string,
  rules: ArabicPhraseRule[],
  singular: string,
  plural: string
): string => {
  const ordered = [...rules].sort((a, b) => b.phrase.length - a.phrase.length);
  let result = defaultMessage;
  for (const rule of ordered) {
    if (!result.includes(rule.phrase)) continue;
    const replacement = rule.form === "plural" ? plural : singular;
    result = result.split(rule.phrase).join(replacement);
  }
  return result;
};

const applyConsumerArabicLabels = (
  defaultMessage: string,
  singular: string,
  plural: string
): string =>
  applyArabicPhraseRules(defaultMessage, AR_CONSUMER_PHRASES, singular, plural);

const applyBeneficiaryArabicLabels = (
  defaultMessage: string,
  singular: string,
  plural: string
): string =>
  applyArabicPhraseRules(
    defaultMessage,
    AR_BENEFICIARY_PHRASES,
    singular,
    plural
  );

const messageMentionsConsumer = (
  messageId: string,
  defaultMessage: string,
  lang: Lang
): boolean => {
  if (BENEFICIARY_ENTITY_MESSAGE_IDS.has(messageId)) return false;
  if (messageId.startsWith("CUSTOMER.")) return true;
  if (messageId.startsWith("GENERAL.")) {
    if (lang === "en") {
      return /\bcustomers?\b/i.test(defaultMessage);
    }
    return /(العملاء|العميل|عملاء|عميل|مستهلك)/u.test(defaultMessage);
  }
  const prefixes = ["INVOICES.", "SUBSCRIPTION.", "PAYMENT_LINK.", "HOME."];
  if (!prefixes.some((p) => messageId.startsWith(p))) return false;
  if (lang === "en") {
    return /\bcustomers?\b/i.test(defaultMessage);
  }
  return /(العملاء|العميل|عملاء|عميل|مستهلك)/u.test(defaultMessage);
};

const messageMentionsBeneficiary = (
  messageId: string,
  defaultMessage: string,
  lang: Lang
): boolean => {
  if (messageId.startsWith("BENEFICIARY.")) return true;
  if (BENEFICIARY_ENTITY_MESSAGE_IDS.has(messageId)) return true;
  if (SETTINGS_DISCOUNT_BENEFICIARY_MESSAGE_IDS.has(messageId)) return true;
  if (!messageId.startsWith("CUSTOMER.")) return false;
  if (ASSOCIATED_ENTITY_MESSAGE_IDS.has(messageId)) return true;
  if (lang === "en") {
    return /\b(beneficiaries?|associated entities?|\bentities?\b)/i.test(
      defaultMessage
    );
  }
  return /(مستفيد|الكيان|كيان جديد|كيان مرتبط|كيانات مرتبطة)/u.test(
    defaultMessage
  );
};

type IntlWithResourceLabel = {
  formatMessage: (
    descriptor: { id: string },
    values?: Record<string, unknown>
  ) => string;
  getResourceLabel?: (
    resourceKey: string,
    form?: TerminologyResourceForm
  ) => string | undefined;
};

/** Pass shimmed `useIntl()` — `formatMessage` overload is wider than `string`. */
export type BeneficiaryHeadingIntl = IntlWithResourceLabel & {
  formatMessage: (...args: unknown[]) => string;
};

/** Section heading when listing linked beneficiaries (count-aware singular/plural). */
export const getBeneficiaryHeadingLabel = (
  intl: unknown,
  beneficiaryCount: number
): string => {
  const { formatMessage, getResourceLabel } = intl as BeneficiaryHeadingIntl;
  const form: TerminologyResourceForm =
    beneficiaryCount > 1 ? "plural" : "singular";
  const fromTerminology = getResourceLabel?.("beneficiary", form);
  if (fromTerminology) {
    return fromTerminology;
  }
  return formatMessage({
    id:
      beneficiaryCount > 1
        ? "CUSTOMER.ASSOCIATED_ENTITIES"
        : "CUSTOMER.ASSOCIATED_ENTITY",
  });
};

export const getTerminologyFromSettings = (
  settings?: CompanySettings
): OrganizationTerminology | undefined => settings?.terminology;

export const getTerminologyResourceLabel = (
  terminology: OrganizationTerminology | undefined,
  resourceKey: string,
  form: TerminologyResourceForm,
  lang: Lang
): string | undefined => {
  const resource = terminology?.resources?.[resourceKey];
  if (!resource) return undefined;

  const pick = (
    label?: TerminologyLocalizedLabel | null
  ): string | undefined => {
    if (!label) return undefined;
    if (lang === "ar" && label.ar?.trim()) return label.ar.trim();
    return label.en?.trim() || undefined;
  };

  const primary = form === "plural" ? resource.plural : resource.singular;
  const fallback =
    form === "plural" && !primary ? resource.singular : resource.plural;

  return pick(primary) ?? pick(fallback);
};

export const resolveTerminologyMessage = (
  messageId: string,
  defaultMessage: string,
  terminology: OrganizationTerminology | undefined,
  lang: Lang
): string | undefined => {
  const direct = TERMINOLOGY_MESSAGE_MAP[messageId];
  if (direct) {
    return getTerminologyResourceLabel(
      terminology,
      direct.resource,
      direct.form,
      lang
    );
  }

  if (ASSOCIATED_ENTITY_MESSAGE_IDS.has(messageId)) {
    const label = getTerminologyResourceLabel(
      terminology,
      "beneficiary",
      "singular",
      lang
    );
    if (label) {
      return lang === "en"
        ? applyBeneficiaryLabelToMessage(defaultMessage, label)
        : applyBeneficiaryArabicLabels(
            defaultMessage,
            label,
            getTerminologyResourceLabel(
              terminology,
              "beneficiary",
              "plural",
              lang
            ) ?? label
          );
    }
  }

  const consumerSingular = getTerminologyResourceLabel(
    terminology,
    "consumer",
    "singular",
    lang
  );
  const beneficiarySingular = getTerminologyResourceLabel(
    terminology,
    "beneficiary",
    "singular",
    lang
  );

  const beneficiaryPluralResolved = beneficiarySingular
    ? (getTerminologyResourceLabel(
        terminology,
        "beneficiary",
        "plural",
        lang
      ) ?? beneficiarySingular)
    : undefined;

  if (
    beneficiarySingular &&
    beneficiaryPluralResolved &&
    messageMentionsBeneficiary(messageId, defaultMessage, lang)
  ) {
    return lang === "en"
      ? applyBeneficiaryEnglishLabels(
          defaultMessage,
          beneficiarySingular,
          beneficiaryPluralResolved
        )
      : applyBeneficiaryArabicLabels(
          defaultMessage,
          beneficiarySingular,
          beneficiaryPluralResolved
        );
  }

  if (
    consumerSingular &&
    messageMentionsConsumer(messageId, defaultMessage, lang)
  ) {
    const consumerPlural =
      getTerminologyResourceLabel(terminology, "consumer", "plural", lang) ??
      consumerSingular;
    return lang === "en"
      ? applyConsumerEnglishLabels(
          defaultMessage,
          consumerSingular,
          consumerPlural
        )
      : applyConsumerArabicLabels(
          defaultMessage,
          consumerSingular,
          consumerPlural
        );
  }

  return undefined;
};
